import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { FAN_FAVOURITE, FLAVOURS } from "@/data/content";
import { FanBadge } from "./FanBadge";

const N = FLAVOURS.length;
const STEP = 360 / N;
const DOTS = Array.from({ length: 14 }, (_, i) => ({ a: (i / 14) * Math.PI * 2, d: 140 + (i % 3) * 50, s: 10 + (i % 4) * 7 }));

const Burst = ({ color, k }) => (
  <div aria-hidden className="pointer-events-none absolute left-1/2 top-[14%] z-0">
    {DOTS.map((p, i) => (
      <motion.span key={`${k}-${i}`} className="absolute rounded-full" style={{ width: p.s, height: p.s, background: i % 3 ? color : "#FFF6EA" }}
        initial={{ x: 0, y: 0, opacity: 0.9, scale: 0.4 }} animate={{ x: Math.cos(p.a) * p.d, y: Math.sin(p.a) * p.d, opacity: 0, scale: 1 }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} />
    ))}
  </div>
);

const WheelBag = ({ f, i, rot, active, pick }) => {
  const counter = useTransform(rot, (r) => -r - i * STEP);
  return (
    <div className="absolute left-1/2 top-1/2 h-0 w-0" style={{ transform: `rotate(${i * STEP}deg) translateY(calc(min(86vw,520px) / -2.4))` }}>
      <motion.div style={{ rotate: counter }} className="absolute -translate-x-1/2 -translate-y-1/2">
        <motion.button type="button" onClick={() => pick(i)} aria-label={`Show ${f.name}`} data-testid={`flavour-wheel-bag-${f.key}`}
          animate={{ scale: i === active ? 1.2 : 0.62, opacity: i === active ? 1 : 0.6 }} whileHover={{ scale: i === active ? 1.24 : 0.72, opacity: 1 }}
          transition={{ type: "spring", stiffness: 160, damping: 18 }} className="block w-[24vw] max-w-[150px] cursor-pointer focus:outline-none">
          <img src={f.img} alt={`${f.name} bag`} draggable={false} className="w-full drop-shadow-[0_24px_30px_rgba(27,21,48,0.35)]" />
        </motion.button>
      </motion.div>
    </div>
  );
};

const Wheel = ({ rot, active, pick }) => (
  <motion.div className="relative mx-auto aspect-square w-[min(86vw,520px)] rounded-full border-2 border-dashed border-[#1B1530]/15" style={{ rotate: rot }}>
    {FLAVOURS.map((f, i) => <WheelBag key={f.key} f={f} i={i} rot={rot} active={active} pick={pick} />)}
  </motion.div>
);

const Arrow = ({ dir, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`flavour-wheel-${dir}`} aria-label={dir === "prev" ? "Previous flavour" : "Next flavour"}
    className="grid h-11 w-11 place-items-center rounded-full bg-white border border-[#1B1530]/15 hover:bg-[#1B1530] hover:text-white transition-colors">
    {dir === "prev" ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

export const FlavourWheel = () => {
  const [active, setActive] = useState(0);
  const [target, setTarget] = useState(0);
  const rot = useSpring(0, { stiffness: 60, damping: 16 });
  // spin the short way round to the picked bag
  const pick = (i) => {
    const delta = ((i - active + N + Math.floor(N / 2)) % N) - Math.floor(N / 2);
    if (!delta) return;
    const next = target - delta * STEP;
    setTarget(next);
    rot.set(next);
    setActive(((i % N) + N) % N);
  };
  const f = FLAVOURS[active];

  return (
    <section data-testid="home-flavours-section" className="relative">
      <motion.div className="overflow-hidden py-20 lg:py-24" animate={{ backgroundColor: `${f.color}33` }} transition={{ duration: 0.6 }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-5 relative z-10">
            <p className="eyebrow text-[#FF5A5F]">The flavour wheel · {active + 1}/{N}</p>
            <div className="relative mt-3 h-[5.6rem] sm:h-[8.5rem]">
              <AnimatePresence initial={false}>
                <motion.h2 key={f.key} data-testid="flavour-wheel-name" className="absolute inset-0 font-display text-5xl sm:text-7xl leading-[0.9]"
                  initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -40, opacity: 0 }} transition={{ duration: 0.45 }}>
                  {f.name}
                </motion.h2>
              </AnimatePresence>
            </div>
            <p className="mt-3 text-base sm:text-lg text-[#4A4560]">{f.notes}. 22g protein, 75% less sugar than leading candy, sweetened with honey.</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link to={`/flavours/${f.key}`} data-testid="flavour-wheel-shop" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#1B1530] text-white font-bold hover:bg-[#FF5A5F] transition-colors">Shop {f.name} <ArrowRight className="h-4 w-4" /></Link>
              {f.isNew && <span className="rounded-full bg-[#FF5A5F] text-white text-xs font-bold uppercase px-3 py-1">New</span>}
              {f.key === FAN_FAVOURITE && <FanBadge />}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <Arrow dir="prev" onClick={() => pick(active - 1)} />
              <div className="flex gap-1.5">{FLAVOURS.map((x, i) => <button key={x.key} type="button" onClick={() => pick(i)} data-testid={`flavour-wheel-dot-${x.key}`} aria-label={`Show ${x.name}`} className="h-2 rounded-full transition-[width,background-color] duration-300" style={{ width: i === active ? 28 : 10, background: i === active ? "#1B1530" : "#1B153033" }} />)}</div>
              <Arrow dir="next" onClick={() => pick(active + 1)} />
            </div>
            <p className="mt-3 text-sm text-[#4A4560]">Tap a bag to spin the wheel.</p>
          </div>
          <div className="lg:col-span-7 relative">
            <Burst color={f.color} k={f.key} />
            <Wheel rot={rot} active={active} pick={pick} />
          </div>
        </div>
      </motion.div>
    </section>
  );
};
